import { useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";

import { usePagedList } from "../../hook/usePagedList";
import ClientGroupFilter from "../../components/ui/ClientGroupFilter";
import Pager from "../../components/ui/Pager";
import ProviderBadge from "../../components/ui/ProviderBadge";
import { relativeTime, exactTime } from "../../utils/time";
import { useSearchInput } from "../../hook/useSearchInput";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * People, globally.
 *
 * Not scoped to a client by default, because an identity belongs to none: the
 * same PINFL may be a member of several. Filtering by client therefore goes
 * through membership, and answers "who can read this client's discussions"
 * rather than "who was created by it".
 *
 * Filtered and paged on the server. A PINFL is personal data and this register
 * grows with every person who ever signs in, so the browser is never handed
 * more than a page of it.
 *
 * A row opens the person page (/shaxslar/<id>): memberships, messages, logins,
 * phones and P2P chats, and the actions on them.
 */
const IdentitiesPage = () => {
  const [scope, setScope] = useState({ client: "", group: "" });
  const { input, setInput, search, setSearch } = useSearchInput();
  const [provider, setProvider] = useState("");
  const [perPage, setPerPage] = useState(10);
  const navigate = useNavigate();

  // `search` follows the box once typing pauses (useSearchInput): `q` is an
  // ILIKE over name and PINFL with no trigram index, so each request is a scan.
  const params = useMemo(
    () => ({
      ...(scope.client ? { client: scope.client } : {}),
      ...(scope.group ? { group: scope.group } : {}),
      ...(provider ? { provider } : {}),
      ...(search ? { q: search } : {}),
    }),
    [scope.client, scope.group, provider, search],
  );

  const { rows, count, loading, error, page, setPage, totalPages } = usePagedList(
    "/identities/",
    params,
    perPage,
  );

  const filtering = Boolean(scope.client || scope.group || provider || search);
  const from = count === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, count);

  return (
    <div className="ms-panel">
      <form
        className="ms-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(input.trim());
        }}
      >
        <div className="ms-search">
          <Icon icon="tabler:search" />
          <input
            type="text"
            placeholder="PINFL yoki ism..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Qidiruv"
          />
        </div>

        <ClientGroupFilter
          client={scope.client}
          group={scope.group}
          onChange={setScope}
        />

        <select
          className="ms-select ms-select--sm"
          value={provider}
          aria-label="Provayder"
          onChange={(e) => setProvider(e.target.value)}
        >
          <option value="">Barcha provayderlar</option>
          <option value="myid">MyID</option>
          <option value="oneid">OneID</option>
          <option value="client">client (ticket)</option>
        </select>

        {filtering && (
          <button
            type="button"
            className="ms-btn ms-btn--ghost"
            onClick={() => {
              setScope({ client: "", group: "" });
              setProvider("");
              setInput("");
              setSearch("");
            }}
          >
            <Icon icon="tabler:x" />
            Tozalash
          </button>
        )}

        <div className="ms-toolbar-spacer" />
      </form>

      {loading ? (
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      ) : error ? (
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="ms-state">
          <Icon icon="tabler:user-off" />
          <span>{filtering ? "Hech narsa topilmadi" : "Shaxslar yoʻq"}</span>
        </div>
      ) : (
        <>
          <div className="ms-table-wrap">
            <table className="ms-table ms-table--compact">
              <thead>
                <tr>
                  <th>Shaxs</th>
                  <th style={{ width: 190 }}>Provayder</th>
                  <th style={{ width: 100 }}>Tizimlar</th>
                  <th style={{ width: 150 }}>Oxirgi xabar</th>
                  <th style={{ width: 150 }}>Birinchi kirgan</th>
                  <th style={{ width: 44 }} aria-label="Ochish" />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const open = () => navigate(`/shaxslar/${p.id}`);
                  return (
                    <tr
                      key={p.id}
                      className="is-clickable"
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={(e) => e.key === "Enter" && open()}
                    >
                      <td>
                        <div className="ms-namecell">
                          <Link
                            to={`/shaxslar/${p.id}`}
                            className="ms-sublink"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <b>{p.full_name || "—"}</b>
                          </Link>
                          <span>{p.pinfl}</span>
                        </div>
                      </td>
                      <td>
                        <ProviderBadge provider={p.provider} assertedBy={p.asserted_by_name} />
                      </td>
                      <td className="ms-td-num">
                        {p.membership_count ? (
                          p.membership_count
                        ) : (
                          <span className="ms-td-muted" title="Hech bir mijoz tizimida aʼzo emas">
                            0
                          </span>
                        )}
                      </td>
                      <td className="ms-td-muted" title={exactTime(p.last_message_at)}>
                        {p.last_message_at ? relativeTime(p.last_message_at) : "—"}
                      </td>
                      <td className="ms-td-muted" title={exactTime(p.created_at)}>
                        {relativeTime(p.created_at)}
                      </td>
                      <td>
                        <span className="ms-rowgo">
                          <Icon icon="tabler:chevron-right" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ms-tablefoot">
            <span className="ms-tablefoot-count">
              Koʻrsatilmoqda <b>{from}</b>—<b>{to}</b> / jami <b>{count}</b> ta
            </span>

            <div className="ms-tablefoot-right">
              <label className="ms-perpage">
                <span>Koʻrsatish</span>
                <select
                  className="ms-select ms-select--sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <Pager page={page} totalPages={totalPages} onPage={setPage} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IdentitiesPage;
